/**
 * Where a frequency sits on the spectrogram's vertical axis.
 *
 * The axis is **not linear**. The server divides `[minFrequency, maxFrequency]`
 * into equal steps of whatever `binScale` the manifest declares — mel by
 * default — and renders one image row per step. So a ruler that spaced its ticks
 * linearly would be confidently, quietly wrong: at 24 kHz a mel axis puts 4 kHz
 * at 65% of the height, where a linear one would put it at 33%.
 *
 * This is the single implementation of that mapping. The frequency ruler places
 * its ticks with it, and `drawSpectrogram` crops tiles with it, so the two
 * cannot disagree about where a frequency is — which is exactly the bug that
 * would otherwise look like a rendering fault rather than a units mismatch.
 *
 * It must also match the server's `buildBinMap`; a test asserts that against the
 * reference implementation's own bin edges.
 */

const hzToMel = (hz) => 2595 * Math.log10(1 + hz / 700);
const melToHz = (mel) => 700 * (10 ** (mel / 2595) - 1);

/**
 * Fraction of the axis height a frequency sits at.
 *
 * **0 is the bottom (lowest frequency), 1 is the top.** The tile image is the
 * other way up — row 0 is the highest frequency, so it draws top-down without
 * flipping — so anything converting to an image row wants `1 - fraction`.
 *
 * @param {number} hz
 * @param {number} minFrequency - bottom of the axis; 20 Hz in practice, not 0
 * @param {number} maxFrequency - top; min(20000, sampleRate/2)
 * @param {string} [scale="mel"] - "mel", "log" or "linear", from the manifest
 * @returns {number} unclamped, so a frequency outside the range reports where it
 *   would be — which is what lets a caller decide to cull rather than clip
 */
export function frequencyToFraction(hz, minFrequency, maxFrequency, scale = "mel") {
  if (scale === "log") {
    // Undefined at zero, so a log axis cannot start there. The server's 20 Hz
    // floor is what keeps this well-formed.
    const lo = Math.max(minFrequency, 1e-6);
    const value = Math.max(hz, 1e-6);
    return Math.log(value / lo) / Math.log(maxFrequency / lo);
  }

  if (scale === "linear") {
    return (hz - minFrequency) / (maxFrequency - minFrequency);
  }

  const lo = hzToMel(minFrequency);
  return (hzToMel(hz) - lo) / (hzToMel(maxFrequency) - lo);
}

/**
 * The inverse: what frequency is at a fraction of the axis height.
 *
 * @param {number} fraction - 0 at the bottom, 1 at the top
 */
export function fractionToFrequency(fraction, minFrequency, maxFrequency, scale = "mel") {
  if (scale === "log") {
    const lo = Math.max(minFrequency, 1e-6);
    return lo * (maxFrequency / lo) ** fraction;
  }

  if (scale === "linear") {
    return minFrequency + fraction * (maxFrequency - minFrequency);
  }

  const lo = hzToMel(minFrequency);
  return melToHz(lo + fraction * (hzToMel(maxFrequency) - lo));
}

/**
 * Hz below a thousand, kHz at and above, with no trailing zeros.
 *
 * `900 Hz`, `1 kHz`, `2.5 kHz`, `12 kHz`.
 *
 * `precise` keeps up to three decimals of kHz (`1.025 kHz`) — for labelling
 * the ends of a zoomed band, where the default rounding would print 1000 and
 * 1050 as the same "1 kHz"/"1.1 kHz" pair the reader can't trust.
 */
export function formatFrequency(hz, precise = false) {
  if (hz < 1000) {
    return `${Math.round(hz)} Hz`;
  }

  const k = hz / 1000;
  if (precise) return `${parseFloat(k.toFixed(3))} kHz`;
  // One decimal below 10 kHz, none above — 12.3 kHz is more precision than the
  // tick spacing up there can justify.
  const text = k < 10 ? k.toFixed(1).replace(/\.0$/, "") : String(Math.round(k));
  return `${text} kHz`;
}

export { hzToMel, melToHz };
